import React from 'react';
import styled from 'styled-components';
import { RETENTION_BASIC_VIEW_KEYS } from '../../../utils/builder';
import * as BuilderInfoUI from '../BuilderInfoUI';
import InfoPopupTriggerSubject from './InfoPopupTriggerSubject';
import { useAppDispatch } from '../../../redux/hooks';
import { DateKindKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import {
  getCapitalizedTimeUnitText,
  getQueryTypePagePath,
} from '../../../utils';
import { setCachedFunnel } from '../../../redux/slices/cachedEntities';
import { createFunnelBuilderStateFromAnalysis } from '../../../utils/hooks/useAnalysisInfoFetch/utils';
import { isTransientAnalysisId } from '../../../redux/utils/transientAnalysis';
import { useFullscreenContext } from '../../../utils/contexts/FullscreenContext';
import { getBinningDisplay } from '../../../utils/binnings';
import { getComparisonRangeDisplay } from '../../../utils/comparisonConfig';
import { IStateHint } from '../../../utils/stateHint';
import ShowInReport from './ShowInReport';
import { assertCondition, assertNotNil } from '../../../utils/types';
import useAnalysisBuilderInfo from '../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { IParsedChartData } from '../../Chart/interfaces';
import DatePickerDisplay from '../../Builder/DatePickerWidget/DateRangeDisplay/DatePickerDisplay';
import { datePickerFromAnalysisInfo } from '../../Builder/DatePickerWidget/utils';
import DateDisplay from '../../Builder/DatePickerWidget/DateRangeDisplay/DateDisplay';

interface IProps {
  analysisId: string;
  onShowInReport?: () => void;
  chartData?: IParsedChartData;
}

const Container = styled(BuilderInfoUI.Content)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 4px;
`;

export default ({ analysisId, onShowInReport, chartData }: IProps) => {
  const dispatch = useAppDispatch();

  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertNotNil(analysisBuilderInfo);
  const {
    query: builderInfo,
    queryType,
    start: startDate,
    end: endDate,
    timeUnit,
    datePicker,
  } = analysisBuilderInfo;

  const reportUrl = getQueryTypePagePath(queryType, analysisId);

  const { isFullScreen, toggleFullScreen } = useFullscreenContext();

  const showInReportState: IStateHint =
    chartData && chartData.queryType === QueryType.Funnel
      ? chartData.showInReportState
      : { isDisabled: false };

  const isTransientAnalysis = isTransientAnalysisId(analysisId);
  const initializeBuilderStateFromFunnel =
    !showInReportState.isDisabled &&
    isTransientAnalysis &&
    chartData &&
    chartData.queryType === QueryType.Funnel;

  const navigateToReport = (event: React.MouseEvent<HTMLElement>) => {
    if (isFullScreen) {
      toggleFullScreen();
    }

    if (onShowInReport) {
      onShowInReport();
    }

    // We shouldn't run any extra logic on Ctrl/Command+Click
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (initializeBuilderStateFromFunnel) {
      assertCondition(builderInfo.__typename === 'FunnelQuery');
      dispatch(
        setCachedFunnel(
          createFunnelBuilderStateFromAnalysis({
            id: null, // create a transient builder w/o data
            timeUnit,
            end: endDate,
            start: startDate,
            query: builderInfo,
            datePicker,
          }),
        ),
      );
    }
  };

  return (
    <>
      <Container>
        {'dateKind' in builderInfo && builderInfo.dateKind && (
          <>
            <BuilderInfoUI.BorderedInfo>
              {builderInfo.dateKind.name}
            </BuilderInfoUI.BorderedInfo>
            {typeof builderInfo?.timeWindow === 'number' &&
              queryType !== QueryType.Retention && (
                <BuilderInfoUI.BorderedInfo>
                  {`Time window: ${builderInfo.timeWindow} day(s)`}
                </BuilderInfoUI.BorderedInfo>
              )}
          </>
        )}
        {queryType !== QueryType.Retention && (
          <>
            {timeUnit &&
              (queryType === QueryType.Expression ||
                queryType === QueryType.Funnel) && (
                <BuilderInfoUI.BorderedInfo>
                  {getCapitalizedTimeUnitText(timeUnit).adverb}
                </BuilderInfoUI.BorderedInfo>
              )}
            <BuilderInfoUI.BorderedInfo>
              <DatePickerDisplay
                datePicker={datePickerFromAnalysisInfo(analysisBuilderInfo)}
                hideFixedDateRange={true}
              />
            </BuilderInfoUI.BorderedInfo>
            <BuilderInfoUI.BorderedInfo>
              <DateDisplay date={startDate} />
              <span>-</span>
              <DateDisplay date={endDate} />
            </BuilderInfoUI.BorderedInfo>
          </>
        )}
        {'comparisonConfig' in analysisBuilderInfo &&
          analysisBuilderInfo.comparisonConfig && (
            <BuilderInfoUI.BorderedInfo>
              Compare:{' '}
              {getComparisonRangeDisplay(analysisBuilderInfo.comparisonConfig, {
                startDate,
                endDate,
                datePicker: datePickerFromAnalysisInfo(analysisBuilderInfo),
              })}
            </BuilderInfoUI.BorderedInfo>
          )}
        {'includeMissingDates' in builderInfo && (
          <>
            <BuilderInfoUI.InfoLabel>Missing dates</BuilderInfoUI.InfoLabel>
            <BuilderInfoUI.BorderedInfo>
              {builderInfo.includeMissingDates ? 'Included' : 'Excluded'}
            </BuilderInfoUI.BorderedInfo>
          </>
        )}
        {queryType === QueryType.Retention &&
          builderInfo.__typename === 'RetentionQuery' &&
          RETENTION_BASIC_VIEW_KEYS.includes(builderInfo.view.key) && (
            <>
              <BuilderInfoUI.BorderedInfo>
                {builderInfo.calculation.name}
              </BuilderInfoUI.BorderedInfo>

              <BuilderInfoUI.InfoLabel>Calculated by</BuilderInfoUI.InfoLabel>
              <BuilderInfoUI.BorderedInfo>
                {builderInfo.calculationWindow.name}
              </BuilderInfoUI.BorderedInfo>
            </>
          )}
        {queryType === QueryType.Expression &&
          builderInfo.__typename === 'ExpressionQuery' && (
            <>
              {builderInfo?.subject?.name && (
                <>
                  <BuilderInfoUI.InfoLabel>Subject</BuilderInfoUI.InfoLabel>
                  <BuilderInfoUI.BorderedInfo>
                    {builderInfo.subject.name}
                  </BuilderInfoUI.BorderedInfo>
                </>
              )}
              {builderInfo?.subjectBucketDef && (
                <>
                  <BuilderInfoUI.InfoLabel>Bucketing</BuilderInfoUI.InfoLabel>
                  <BuilderInfoUI.BorderedInfo>
                    {getBinningDisplay(builderInfo.subjectBucketDef)}
                  </BuilderInfoUI.BorderedInfo>
                </>
              )}
            </>
          )}
      </Container>
      {'dateKind' in builderInfo &&
        builderInfo?.dateKind?.key === DateKindKey.Trigger &&
        'triggerSubject' in builderInfo &&
        builderInfo.triggerSubject &&
        'triggerCondition' in builderInfo &&
        builderInfo.triggerCondition && (
          <InfoPopupTriggerSubject
            subjectName={builderInfo.triggerSubject.name}
            triggerCondition={builderInfo?.triggerCondition}
          />
        )}
      <BuilderInfoUI.FooterContainer>
        {showInReportState.isDisabled ? (
          // Using wrapper element because does not accept title attribute.
          // Using plain popup because our rich popup does not position
          // properly when rendered inside another absolute container
          <span title={showInReportState.hint}>
            <BuilderInfoUI.PopupButton
              size="small"
              isGreyedOut={true}
              trackContext="report tooltip"
              trackAction="show in report"
            >
              Show in Report
            </BuilderInfoUI.PopupButton>
          </span>
        ) : (
          <ShowInReport
            reportUrl={reportUrl}
            onClick={navigateToReport}
            trackContext="report tooltip"
          />
        )}
      </BuilderInfoUI.FooterContainer>
    </>
  );
};
